import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  LinkOff as UnlinkIcon,
  Policy as PolicyIcon,
} from '@mui/icons-material';
import { gpoAPI } from '../services/api';
import DataSource from '../components/DataSource';
import { formatCreated } from '../utils/formatCreated';
import PaginationControls from '../components/PaginationControls';
import ColumnPicker from '../components/ColumnPicker';
import { useColumnPrefs } from '../hooks/useColumnPrefs';
import {
  fetchGPOs,
  fetchOUs,
  addGPOOptimistic,
  removeGPOOptimistic,
} from '../store/dataSlice';

function GPO() {
  const dispatch = useDispatch();
  const gpos = useSelector((state) => state.data.gpos.data);
  const gposSource = useSelector((state) => state.data.gpos.source);
  const ous = useSelector((state) => state.data.ous.data);
  const loading = useSelector((state) => state.data.gpos.loading || state.data.ous.loading);
  const reduxError = useSelector((state) => state.data.gpos.error || state.data.ous.error);


  const [tabValue, setTabValue] = useState(0);

  // Client-side paging: list_gpos returns the whole set in one response.

  const [page, setPage] = useState(1);

  const [pageSize, setPageSize] = useState(25);
  // Paging derived from the full list the store holds.
  const gpoTotalPages = Math.max(1, Math.ceil((gpos?.length || 0) / pageSize));
  const pagedGpos = (gpos || []).slice((page - 1) * pageSize, page * pageSize);

  const GPO_COLUMNS = React.useMemo(() => [
    { key: 'name', label: 'Name', render: (g) => g.name },
    { key: 'display_name', label: 'Display Name', render: (g) => g.display_name || '-' },
    { key: 'path', label: 'Path',
      sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
      render: (g) => g.path || '-' },
    { key: 'status', label: 'Status', render: (g) => (
        <Chip size="small" label={g.gpoStatus || 'unknown'}
              color={g.gpoStatus === 'enabled' ? 'success'
                     : g.gpoStatus === 'disabled' ? 'default' : 'warning'} />
      ) },
    { key: 'created', label: 'Created', sx: { whiteSpace: 'nowrap' },
      render: (g) => formatCreated(g.whenCreated) },
    { key: 'version', label: 'Version', render: (g) => g.version ?? '-' },
  ], []);
  const { ordered, visibleColumns, toggle, move, reset } =
    useColumnPrefs('cols.gpos', GPO_COLUMNS);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [currentGPO, setCurrentGPO] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    comment: '',
    container_dn: ''
  });
  const [gpoLinks, setGpoLinks] = useState([]);
  const [selectedContainer, setSelectedContainer] = useState(null);

  useEffect(() => {
    // Load cached data or fetch if cache expired
    dispatch(fetchGPOs());
    dispatch(fetchOUs());
  }, [dispatch]);

  const handleRefresh = () => {
    // Force refresh bypassing cache
    dispatch(fetchGPOs(true));
    dispatch(fetchOUs(true));
  };

  const handleOpenDialog = (mode, gpo = null) => {
    setDialogMode(mode);
    setCurrentGPO(gpo);
    if (mode === 'create') {
      setFormData({ name: '', description: '', comment: '', container_dn: '' });
    } else if (mode === 'link' || mode === 'unlink') {
      setFormData({ name: '', description: '', comment: '', container_dn: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentGPO(null);
    setFormData({ name: '', description: '', comment: '', container_dn: '' });
    setSelectedContainer(null);
  };

  const handleSubmit = async () => {
    try {
      if (dialogMode === 'create') {
        if (!formData.name || formData.name.trim() === '') {
          setError('GPO name is required');
          return;
        }
        await gpoAPI.create({ name: formData.name });
        // Optimistic update
        dispatch(addGPOOptimistic({
          name: formData.name,
        }));
      } else if (dialogMode === 'link') {
        if (!selectedContainer) {
          setError('Please select a container (OU or domain)');
          return;
        }
        await gpoAPI.link({
          gpo_name: currentGPO.name,
          container_dn: selectedContainer.dn,
        });
      } else if (dialogMode === 'unlink') {
        if (!selectedContainer) {
          setError('Please select a container to unlink from');
          return;
        }
        await gpoAPI.unlink({
          gpo_name: currentGPO.name,
          container_dn: selectedContainer.dn,
        });
      }
      handleCloseDialog();
      // Refresh in background to ensure data is accurate
      dispatch(fetchGPOs(true));
    } catch (err) {
      setError(err.response?.data?.error || 'Operation failed');
      // Revert optimistic update on error
      dispatch(fetchGPOs(true));
    }
  };

  const handleDelete = async (gpoName) => {
    if (!window.confirm(`Are you sure you want to delete GPO "${gpoName}"?`)) {
      return;
    }
    try {
      // Optimistic update
      dispatch(removeGPOOptimistic(gpoName));
      await gpoAPI.delete(gpoName);
      // Refresh in background
      dispatch(fetchGPOs(true));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete GPO');
      // Revert on error
      dispatch(fetchGPOs(true));
    }
  };

  const loadGPOLinks = async (containerDn) => {
    try {
      const response = await gpoAPI.getLinks(containerDn);
      setGpoLinks(response.data.links || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load GPO links');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3,
                 flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4">Group Policy Objects</Typography>
          <DataSource source={gposSource} sx={{ mt: 0.5 }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <ColumnPicker columns={ordered} onToggle={toggle} onMove={move} onReset={reset} />
          <IconButton onClick={handleRefresh} sx={{ mr: 1 }} title="Refresh">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('create')}
          >
            Create GPO
          </Button>
        </Box>
      </Box>

      {(error || reduxError) && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error || reduxError}
        </Alert>
      )}

      <Paper>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Group Policies" />
          <Tab label="GPO Links" />
        </Tabs>

        {tabValue === 0 && (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  {visibleColumns.map((c) => (
                    <TableCell key={c.key}>{c.label}</TableCell>
                  ))}
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gpos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 1} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No Group Policy Objects found. Click "Create GPO" to add one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedGpos.map((gpo) => (
                    <TableRow key={gpo.name}>
                      {visibleColumns.map((c) => (
                        <TableCell key={c.key} sx={c.sx}>{c.render(gpo)}</TableCell>
                      ))}
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog('link', gpo)}
                          title="Link GPO"
                        >
                          <LinkIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog('unlink', gpo)}
                          title="Unlink GPO"
                        >
                          <UnlinkIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(gpo.name)}
                          title="Delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {tabValue === 0 && (
          <PaginationControls
            count={gpos?.length || 0}
            page={page}
            pageSize={pageSize}
            totalPages={gpoTotalPages}
            onPageChange={(e, p) => setPage(p)}
            onPageSizeChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
          />
        )}

        {tabValue === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Select a container to view its linked GPOs
            </Typography>
            <Autocomplete
              options={ous}
              getOptionLabel={(option) => `${option.name} (${option.dn})`}
              onChange={(event, value) => {
                if (value) {
                  loadGPOLinks(value.dn);
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select OU or Container" margin="normal" />
              )}
              sx={{ mb: 3 }}
            />

            {gpoLinks.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Linked GPOs
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>GPO Name</TableCell>
                        <TableCell>Container DN</TableCell>
                        <TableCell>Link Order</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {gpoLinks.map((link, index) => (
                        <TableRow key={index}>
                          <TableCell>{link.gpo_name}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                            {link.container_dn}
                          </TableCell>
                          <TableCell>{link.link_order || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={link.enabled ? 'Enabled' : 'Disabled'}
                              color={link.enabled ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' && 'Create Group Policy Object'}
          {dialogMode === 'link' && `Link GPO: ${currentGPO?.name}`}
          {dialogMode === 'unlink' && `Unlink GPO: ${currentGPO?.name}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {dialogMode === 'create' && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Creating a Group Policy Object
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    This wizard creates a new GPO container. After creation, you can:
                  </Typography>
                  <Typography variant="body2" component="div">
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      <li>Link the GPO to OUs or domains</li>
                      <li>Configure policies using Group Policy Management Console (GPMC) or PowerShell</li>
                      <li>Set registry-based policies, security settings, and administrative templates</li>
                    </ul>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Documentation:</strong>{' '}
                    <a
                      href="https://wiki.samba.org/index.php/Group_Policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'inherit' }}
                    >
                      Samba Group Policy Guide
                    </a>
                  </Typography>
                </Alert>
                <TextField
                  fullWidth
                  label="GPO Display Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  margin="normal"
                  required
                  helperText="Enter a descriptive name for this Group Policy Object (e.g., 'Desktop Security Policy')"
                />
                <TextField
                  fullWidth
                  label="Description (Optional)"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  margin="normal"
                  multiline
                  rows={2}
                  helperText="Brief description of what this GPO does"
                />
                <TextField
                  fullWidth
                  label="Comment (Optional)"
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  margin="normal"
                  multiline
                  rows={2}
                  helperText="Administrative notes or change history"
                />
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Note:</strong> To configure registry settings and detailed policies, use the Group Policy Management Console (GPMC)
                    from a Windows machine or use samba-tool commands on the server after creating this GPO.
                  </Typography>
                </Alert>
              </>
            )}
            {(dialogMode === 'link' || dialogMode === 'unlink') && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Select the Organizational Unit or container to{' '}
                  {dialogMode === 'link' ? 'link to' : 'unlink from'}
                </Alert>
                <Autocomplete
                  options={ous}
                  getOptionLabel={(option) => `${option.name} (${option.dn})`}
                  value={selectedContainer}
                  onChange={(event, value) => setSelectedContainer(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select OU or Container"
                      margin="normal"
                      required
                    />
                  )}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {dialogMode === 'create' ? 'Create' : dialogMode === 'link' ? 'Link' : 'Unlink'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default GPO;
